import { useQuery } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { objectEntries, objectFromEntries, objectValues } from "@nens/shared-utils";

type FrontendSettingsResponse = {
  authorization_url: string;
  default_client_id: string;
  desktop_client_version: string;
  logout_url: string;
  maptiler_api_key: string;
  native_client_id: string;
  scope: string;
  token_url: string;
  hcc_url: string;
  extract_raster_dataset_process_id: string;
};

// Map snake_case to camelCase for the frontend
const FRONTEND_SETTINGS_MAP = {
  authorization_url: "authorizationUrl",
  default_client_id: "defaultClientId",
  desktop_client_version: "desktopClientVersion",
  logout_url: "logoutUrl",
  maptiler_api_key: "maptilerApiKey",
  native_client_id: "nativeClientId",
  scope: "scope",
  token_url: "tokenUrl",
  hcc_url: "hccUrl",
  extract_raster_dataset_process_id: "extractRasterDatasetProcessId",
} as const satisfies Record<keyof FrontendSettingsResponse, string>;

type FrontendSettings = {
  [Key in keyof FrontendSettingsResponse as (typeof FRONTEND_SETTINGS_MAP)[Key]]: FrontendSettingsResponse[Key];
};

type FrontendSettingsKey = keyof FrontendSettings;

const NULL_FRONTEND_SETTINGS = objectFromEntries(
  objectValues(FRONTEND_SETTINGS_MAP).map((key) => [key, null])
) as Record<FrontendSettingsKey, null>;

function toFrontendSettings(data: FrontendSettingsResponse): FrontendSettings {
  return objectFromEntries(
    objectEntries(data).map(([key, value]) => [FRONTEND_SETTINGS_MAP[key], value])
  ) as FrontendSettings;
}

const fetchFrontendSettings = async (): Promise<FrontendSettingsResponse> => {
  return (await getRanaApiV1().BaseApi.fetchJson("/frontend-settings")) as FrontendSettingsResponse;
};

export function useFrontendSetting() {
  const query = useQuery(
    getRanaApiV1().getQueryOptions({
      ignoreTenant: true,
      queryKey: ["frontend-settings"],
      queryFn: fetchFrontendSettings,
    })
  );

  if (query.isSuccess) {
    const { data, ...rest } = query;
    return { ...toFrontendSettings(data), ...rest };
  }

  const { data, ...rest } = query;
  return { ...NULL_FRONTEND_SETTINGS, ...rest };
}
